import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { SwitchField } from '@openx/components-core/lib/SwitchField/SwitchField.tsx';
import type { SupplementalAdQuality as SupplementalAdQualityType } from '@openx/types/deal.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DEAL_SUPPLEMENTS } from './constants.ts';
import { SupplementalAdQualityDetails } from './SupplementalAdQualityDetails.tsx';
import { SupplementalAdQualityDrawer } from './SupplementalAdQualityDrawer.tsx';

export function SupplementalAdQuality() {
  const {
    formState: { isSubmitting },
    setValue,
  } = useFormContext();
  const { t } = useTranslation();
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const observableDealSupplements = useWatch({ name: DEAL_SUPPLEMENTS });

  const onApply = useCallback(
    (value: SupplementalAdQualityType) => {
      setValue(DEAL_SUPPLEMENTS, value, { shouldDirty: true });
      setDrawerOpen(false);
    },
    [setValue]
  );

  const onCancel = useCallback(() => setDrawerOpen(false), []);

  const onSwithChange = useCallback(() => {
    if (!observableDealSupplements) {
      setDrawerOpen(true);
    } else {
      setValue(DEAL_SUPPLEMENTS, null, { shouldDirty: true });
    }
  }, [setValue, observableDealSupplements]);

  const onEdit = useCallback(() => setDrawerOpen(true), []);
  const onRemove = useCallback(() => setValue(DEAL_SUPPLEMENTS, null, { shouldDirty: true }), [setValue]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <SwitchField
          label={t('Supplemental Ad Quality Filters')}
          type="checkbox"
          checked={!!observableDealSupplements || isDrawerOpen}
          onChange={onSwithChange}
          data-test="deal-supplements"
          disabled={isSubmitting}
        />
        <Tooltip
          title={
            <Trans i18nKey={'supplemental_ad_quality_info'}>
              You can add exceptions to the default ad quality filters specifically for a Private Marketplace deal.
              Exceptions will allow ads that would otherwise be blocked, for this deal only.
              <br />
              <br />
              You cannot use supplemental ad quality filters to block ads in a deal.
            </Trans>
          }
          placement="right"
        >
          <IconButton size="small">
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {isDrawerOpen && (
        <SupplementalAdQualityDrawer onApply={onApply} onClose={onCancel} currentState={observableDealSupplements} />
      )}
      {!!observableDealSupplements && (
        <SupplementalAdQualityDetails
          currentState={observableDealSupplements}
          onEdit={onEdit}
          onRemove={onRemove}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
}
